import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Check, Info, PlaneTakeoff, Plus } from 'lucide-react';
import Header from '../components/Header';

import {
  PRICING,
  PRICING_US_HOME,
  PRICING_FR_HOME,
  US_HOME_PLAN_IDS,
  FR_HOME_PLAN_IDS,
  buildUsHomeVolumePlans,
  buildFrHomeVolumePlans,
  type UsHomePlanId,
  type FrHomePlanId,
} from '../lib/pricing';
import {
  US_HOME_PRICING_SECTION,
  US_PARTNERS_PRICING_SECTION,
  US_PARTNER_VOLUME_PLANS,
  FR_HOME_PRICING_SECTION,
  FR_PARTNERS_PRICING_SECTION,
  FR_PARTNER_VOLUME_PLANS,
  type UsPartnerVolumePlanRow,
} from '../lib/usPricingSectionCopy';
import { authApi, getCachedUser, getToken, paymentApi, subscriptionApi } from '../lib/api';

type Tab = 'standard' | 'drone';

type PlanCard = {
  key: string;
  planId: string;
  videos: number;
  savingsLabel: string;
  unitPrice: number;
  monthlyPrice: number;
  popular: boolean;
};

const OnboardingPlan: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = (location.pathname || '').toLowerCase();
  const isFR = pathname.startsWith('/fr');
  const isPartnerMode = isFR
    ? pathname.startsWith('/fr/partenaires')
    : pathname.startsWith('/partners');

  const onboardingPath = isFR
    ? (isPartnerMode ? '/fr/partenaires/onboarding/plan' : '/fr/onboarding/plan')
    : (isPartnerMode ? '/partners/onboarding/plan' : '/onboarding/plan');
  const dashboardPath = isFR
    ? (isPartnerMode ? '/fr/partenaires/dashboard' : '/fr/dashboard')
    : (isPartnerMode ? '/partners/dashboard' : '/dashboard');
  const signupPath = isFR
    ? (isPartnerMode ? '/fr/partenaires/signup' : '/fr/signup')
    : (isPartnerMode ? '/partners/signup' : '/signup');
  const stripeRegion: 'us' | 'fr' = isFR ? 'fr' : 'us';
  const stripeUsPriceSet: 'home' | 'partners' | undefined = isFR ? undefined : (isPartnerMode ? 'partners' : 'home');
  const currency = isFR ? '€' : '$';
  const copy = isFR
    ? (isPartnerMode ? FR_PARTNERS_PRICING_SECTION : FR_HOME_PRICING_SECTION)
    : (isPartnerMode ? US_PARTNERS_PRICING_SECTION : US_HOME_PRICING_SECTION);

  const [tab, setTab] = useState<Tab>('standard');
  const [loadingChecks, setLoadingChecks] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingKey, setProcessingKey] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [userName, setUserName] = useState<string>('');

  const plans: PlanCard[] = useMemo(() => {
    const withDrone = tab === 'drone';

    if (isPartnerMode) {
      const volumePlans = isFR ? FR_PARTNER_VOLUME_PLANS : US_PARTNER_VOLUME_PLANS;
      const rows = withDrone ? volumePlans.drone : volumePlans.standard;
      return rows.map<PlanCard>((p) => ({
        key: `${p.id}-${tab}`,
        planId: p.id,
        videos: p.properties,
        savingsLabel: p.savings,
        unitPrice: p.perProperty,
        monthlyPrice: p.price,
        popular: p.popular,
      }));
    }

    if (isFR) {
      return buildFrHomeVolumePlans(withDrone).map<PlanCard>((p, idx) => ({
        key: `${FR_HOME_PLAN_IDS[idx]}-${tab}`,
        planId: FR_HOME_PLAN_IDS[idx],
        videos: p.videos,
        savingsLabel: p.savingsLabel,
        unitPrice: p.unitPrice,
        monthlyPrice: p.monthlyPrice,
        popular: !!p.popular,
      }));
    }

    const base = withDrone
      ? PRICING_US_HOME.extraCredit.withDrone
      : PRICING_US_HOME.extraCredit.withoutDrone;
    return buildUsHomeVolumePlans(base).map<PlanCard>((p, idx) => ({
      key: `${US_HOME_PLAN_IDS[idx]}-${tab}`,
      planId: US_HOME_PLAN_IDS[idx] as UsHomePlanId,
      videos: p.videos,
      savingsLabel: p.savingsLabel,
      unitPrice: p.unitPrice,
      monthlyPrice: p.monthlyPrice,
      popular: !!p.popular,
    }));
  }, [isPartnerMode, isFR, tab]);

  const trialPrice = (() => {
    const withDrone = tab === 'drone';
    if (isPartnerMode) return withDrone ? PRICING.trial.withDrone : PRICING.trial.withoutDrone;
    if (isFR) return withDrone ? PRICING_FR_HOME.trial.withDrone : PRICING_FR_HOME.trial.withoutDrone;
    return withDrone ? PRICING_US_HOME.trial.withDrone : PRICING_US_HOME.trial.withoutDrone;
  })();

  // Au chargement : auth requise ; redirection si le rôle ne matche pas la page, ou si déjà abonné.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // 1) On se fie au cache local (posé par signup/login/OAuth) et à la présence d'un token.
        //    Ne pas bloquer sur authApi.getUser() : un échec ponctuel effacerait le token et renverrait à /signup.
        const hasToken = !!getToken();
        let user = getCachedUser();
        if (!user && !hasToken) {
          navigate(signupPath, { replace: true });
          return;
        }
        if (!user && hasToken) {
          // Pas de cache mais un token : on tente un refresh non bloquant.
          try {
            user = await authApi.getUser();
          } catch {
            /* on garde le flux, pas de redirection forcée */
          }
          if (cancelled) return;
        }
        if (user) setUserName(user.name || '');

        if (isPartnerMode && user && user.user_type !== 'partner') {
          try {
            await authApi.claimPartnerRole();
          } catch {
            /* ignore */
          }
        }
        if (!isPartnerMode && user?.user_type === 'partner') {
          const partnerDash = isFR ? '/fr/partenaires/dashboard' : '/partners/dashboard';
          navigate(partnerDash, { replace: true });
          return;
        }

        // 2) Check abonnement ; si ça plante (réseau, pas de token encore propagé), on reste sur l'onboarding.
        try {
          const sub = await subscriptionApi.get();
          if (cancelled) return;
          const hasCredits = (sub?.credits_remaining ?? 0) > 0;
          const hasActivePlan = sub?.status === 'active' && !!sub.plan && sub.plan !== 'free';
          if (hasCredits || hasActivePlan) {
            navigate(dashboardPath, { replace: true });
            return;
          }
        } catch {
          /* ignore */
        }
      } finally {
        if (!cancelled) setLoadingChecks(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dashboardPath, isPartnerMode, isFR, signupPath, navigate]);

  const baseOrigin = typeof window !== 'undefined' ? window.location.origin : '';
  const successUrl = baseOrigin + dashboardPath + '?subscription=success';
  const cancelUrl = baseOrigin + onboardingPath;

  const errorMsg = isFR
    ? 'Impossible de démarrer le paiement. Veuillez réessayer.'
    : 'Could not start checkout. Please try again.';

  const handleTrial = async (withDrone: boolean) => {
    if (isProcessing) return;
    setError('');
    setIsProcessing(true);
    setProcessingKey(withDrone ? 'trial-drone' : 'trial-standard');
    try {
      const { url } = await paymentApi.createCheckoutSession({
        region: stripeRegion,
        type: 'trial',
        with_drone: withDrone,
        ...(stripeUsPriceSet ? { stripe_us_price_set: stripeUsPriceSet } : {}),
        success_url: successUrl,
        cancel_url: cancelUrl,
      });
      window.location.href = url;
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : errorMsg);
      setIsProcessing(false);
      setProcessingKey(null);
    }
  };

  const handleSubscription = async (planId: string, withDrone: boolean) => {
    if (isProcessing) return;
    setError('');
    setIsProcessing(true);
    setProcessingKey(`${planId}-${withDrone ? 'drone' : 'standard'}`);
    try {
      const { url } = await paymentApi.createCheckoutSession({
        region: stripeRegion,
        type: 'subscription',
        plan_id: planId,
        with_drone: withDrone,
        ...(stripeUsPriceSet ? { stripe_us_price_set: stripeUsPriceSet } : {}),
        success_url: successUrl,
        cancel_url: cancelUrl,
      });
      window.location.href = url;
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : errorMsg);
      setIsProcessing(false);
      setProcessingKey(null);
    }
  };

  const FeatureRow = ({
    text,
    highlight = false,
    isExtra = false,
    useInfoIcon = false,
  }: {
    text: string;
    highlight?: boolean;
    isExtra?: boolean;
    useInfoIcon?: boolean;
  }) => {
    return (
      <div className="flex items-center justify-between gap-2 py-2 border-b border-gray-50 last:border-0">
        <div className="flex items-center min-w-0 flex-1">
          {useInfoIcon ? (
            <span className="mr-2 sm:mr-3 shrink-0">
              <Info className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
            </span>
          ) : (
            <div
              className={`p-1 rounded-full mr-2 sm:mr-3 shrink-0 ${
                isExtra ? 'bg-[#3C59B2]/10' : highlight ? 'bg-[#3C59B2]/10' : 'bg-green-50'
              }`}
            >
              {isExtra ? (
                <Plus className="w-3 h-3 text-[#3C59B2]" />
              ) : highlight ? (
                <PlaneTakeoff className="w-3 h-3 text-[#3C59B2]" />
              ) : (
                <Check className="w-3 h-3 text-green-500" />
              )}
            </div>
          )}
          <span
            className={`text-xs sm:text-sm whitespace-pre-line ${
              isExtra ? 'font-medium text-[#3C59B2]' : highlight ? 'font-medium text-[#3C59B2]' : 'text-gray-600'
            }`}
          >
            {text}
          </span>
        </div>
      </div>
    );
  };

  const walkthroughFeature = copy.walkthroughFeature;

  const subtitle = isFR
    ? (isPartnerMode
        ? 'Revendez des vidéos à vos clients. Choisissez un forfait mensuel ou essayez un bien.'
        : 'Essayez avec un bien, ou choisissez le forfait mensuel adapté à votre volume.')
    : (isPartnerMode
        ? 'Resell videos to your clients. Pick a monthly plan or try one property first.'
        : 'Try a single property, or pick the monthly plan that fits your volume.');

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-[#3C59B2]/10 flex flex-col">
      <Header variant="logoOnly" />
      <main className="flex-1">
        <section className="py-12 sm:py-16 md:py-20">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto mb-10 sm:mb-12">
              <motion.h1
                className="font-manrope text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-3 sm:mb-4"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                {userName
                  ? (isFR ? `Bienvenue, ${userName.split(' ')[0]} !` : `Welcome, ${userName.split(' ')[0]}!`)
                  : (isFR ? 'Choisissez votre forfait' : 'Choose Your Plan')}
              </motion.h1>
              <motion.p
                className="text-base sm:text-lg md:text-xl text-gray-600"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
              >
                {subtitle}
              </motion.p>

              <div className="mt-6 sm:mt-8 flex flex-col sm:flex-row gap-3 justify-center items-center">
                <button
                  type="button"
                  onClick={() => setTab('standard')}
                  className={`btn-premium shrink-0 whitespace-nowrap px-6 py-3 rounded-full font-semibold text-sm transition-all duration-300 ${
                    tab === 'standard'
                      ? 'bg-[#3C59B2] text-white'
                      : 'bg-white border-2 border-[#3C59B2] text-[#3C59B2] hover:bg-slate-50'
                  }`}
                >
                  {copy.standardTab}
                </button>
                <button
                  type="button"
                  onClick={() => setTab('drone')}
                  className={`btn-premium shrink-0 whitespace-nowrap px-6 py-3 rounded-full font-semibold text-sm transition-all duration-300 ${
                    tab === 'drone'
                      ? 'bg-[#3C59B2] text-white'
                      : 'bg-white border-2 border-[#3C59B2] text-[#3C59B2] hover:bg-slate-50'
                  }`}
                >
                  {copy.droneTab}
                </button>
              </div>

              <div className="mt-6 flex justify-center">
                <div className="inline-flex items-center gap-3 bg-white border border-[#E2E8F0] px-5 py-2.5 rounded-full shadow-sm">
                  <div className="w-5 h-5 rounded-full bg-[#3C59B2] flex items-center justify-center flex-shrink-0">
                    <span className="text-white text-xs font-bold">i</span>
                  </div>
                  <span className="text-sm text-gray-600">{copy.creditsInfo}</span>
                </div>
              </div>
            </div>

            {error ? (
              <div className="max-w-2xl mx-auto mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
                <p className="text-red-800 text-sm text-center">{error}</p>
              </div>
            ) : null}

            {/* Try one property — visible but secondary */}
            <motion.div
              className="max-w-6xl mx-auto mb-10 sm:mb-12"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="bg-white rounded-2xl border border-[#E2E8F0] px-6 sm:px-8 py-5 sm:py-6 flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
                <div className="flex-1 text-center sm:text-left">
                  <h3 className="font-manrope text-lg font-bold text-gray-900">
                    {isFR ? 'Envie de tester ? Commencez avec un bien.' : 'Want to test first? Start with one property.'}
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">
                    {isFR ? '1 vidéo pour ' : '1 video for '}
                    <span className="font-bold text-slate-900 text-base">{currency}{trialPrice}</span>
                    {isFR
                      ? ` — paiement unique, sans abonnement. Inclut version non-brandée, livraison 24h${tab === 'drone' ? ', séquences drone' : ''}.`
                      : ` — one-time, no subscription. Includes unbranded version, 24h delivery${tab === 'drone' ? ', drone clips' : ''}.`}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => handleTrial(tab === 'drone')}
                  disabled={isProcessing || loadingChecks}
                  className="shrink-0 px-6 py-3 rounded-full font-bold text-sm transition-all flex items-center gap-2 bg-white text-[#3C59B2] border-2 border-[#3C59B2] hover:bg-slate-50 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {processingKey === (tab === 'drone' ? 'trial-drone' : 'trial-standard')
                    ? (isFR ? 'Chargement…' : 'Loading…')
                    : (
                        <>
                          {isFR ? 'Essayer un bien' : 'Try One Property'} <ArrowRight className="w-4 h-4" />
                        </>
                      )}
                </button>
              </div>
            </motion.div>

            {/* Monthly subscriptions — primary focus */}
            <div className="max-w-6xl mx-auto">
              <h2 className="font-manrope text-xl sm:text-2xl font-bold text-gray-900 mb-2 text-center">
                {isFR ? 'Économisez avec un forfait mensuel' : 'Save more with a monthly plan'}
              </h2>
              <p className="text-sm sm:text-base text-gray-500 text-center mb-8">
                {isFR ? 'Coût par vidéo réduit. Annulez à tout moment.' : 'Lower per-video cost. Cancel anytime.'}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
                {plans.map((plan, index) => {
                  const key = plan.key;
                  return (
                    <motion.div
                      key={key}
                      className={`card-hover bg-white rounded-3xl p-5 sm:p-6 md:p-8 border transition-all duration-300 relative flex flex-col ${
                        plan.popular ? 'border-[#3C59B2] shadow-xl z-10 md:-translate-y-4' : 'border-[#E2E8F0]'
                      }`}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                    >
                      {plan.savingsLabel ? (
                        <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-green-100 text-green-700 px-4 sm:px-6 py-1.5 rounded-full text-[10px] sm:text-xs font-bold shadow-md whitespace-nowrap">
                          {plan.savingsLabel}
                        </div>
                      ) : null}

                      <div className="text-center mb-6">
                        <h3 className="font-manrope text-lg sm:text-xl md:text-2xl font-bold text-gray-900 mb-3">
                          {plan.videos} {isFR ? 'vidéos/mois' : 'videos/month'}
                        </h3>

                        <div
                          className={`text-3xl sm:text-4xl font-bold ${
                            plan.popular ? 'text-[#3C59B2]' : 'text-slate-900'
                          }`}
                        >
                          {currency}{plan.monthlyPrice.toLocaleString()}<span className="text-base font-semibold text-slate-500">/{isFR ? 'mois' : 'mo'}</span>
                        </div>
                        <div className="text-sm text-slate-500 mt-2">
                          {currency}{plan.unitPrice} {copy.perVideo}
                        </div>
                      </div>

                      <div className="flex-1 space-y-2 mb-6 sm:mb-8 border-t border-gray-100 pt-4 sm:pt-6">
                        <FeatureRow text={`${plan.videos} ${isFR ? 'crédits/mois' : 'credits/month'}`} />
                        <FeatureRow text={walkthroughFeature} />
                        <FeatureRow text={isFR ? 'Version non-brandée incluse' : 'Unbranded version included (MLS-ready)'} />
                        <FeatureRow text={isFR ? 'Livraison 24 h (lun-jeu)' : '24-hour delivery (Mon-Thu)'} />
                        {tab === 'drone' && <FeatureRow text={isFR ? 'Séquences drone incluses' : 'drone clips included'} highlight={true} />}
                        <FeatureRow text={isFR ? 'Crédits à l\'unité disponibles' : 'Additional credits available'} isExtra={true} />
                        <FeatureRow text={isFR ? 'Vendredi → livré lundi' : 'Friday → delivered Monday'} useInfoIcon />
                      </div>

                      <button
                        type="button"
                        onClick={() => handleSubscription(plan.planId, tab === 'drone')}
                        disabled={isProcessing || loadingChecks}
                        className={`btn-premium overflow-hidden w-full py-3 sm:py-4 rounded-full font-bold text-center transition-all flex items-center justify-center gap-2 text-sm sm:text-base disabled:opacity-60 disabled:cursor-not-allowed ${
                          plan.popular
                            ? 'bg-[#3C59B2] text-white hover:bg-[#2d4a8f]'
                            : 'bg-white text-[#3C59B2] border-2 border-[#3C59B2] hover:bg-slate-50'
                        }`}
                      >
                        {processingKey === `${plan.planId}-${tab === 'drone' ? 'drone' : 'standard'}` ? (
                          isFR ? 'Chargement…' : 'Loading…'
                        ) : (
                          <>
                            {isFR ? 'Commencer' : 'Subscribe'} <ArrowRight className="w-4 h-4" />
                          </>
                        )}
                      </button>
                    </motion.div>
                  );
                })}
              </div>
            </div>

          </div>
        </section>
      </main>
    </div>
  );
};

export default OnboardingPlan;
